import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild, computed, inject, input } from '@angular/core';

import { PropertyDefinition } from '../../../models/property-schema';
import { DynamicVariablesService } from '@core/services/dynamic-variables.service';
import { OpenapiPropertiesEditorComponent } from '../../openapi-properties-editor/openapi-properties-editor';
import { SYSTEM_VARIABLE } from '@generated/models/system-variable-array';
import { SystemVariable } from '@generated/models/system-variable';
import { TooltipModule } from 'primeng/tooltip';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-variables-manager',
  imports: [ButtonModule, MenuModule, TooltipModule, OpenapiPropertiesEditorComponent],
  templateUrl: './variables-manager.component.html',
  styleUrls: ['./variables-manager.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariablesManager {
  readonly textElement = input<HTMLTextAreaElement | HTMLInputElement | null>(null);
  readonly text = input<string>('');
  readonly schema = input<any>(null);
  /** Название текущего агента — используется для preview
   *  system__agent_title. Опциональный, потому что VariablesManager
   *  живёт не только в agent-edit; когда null — preview не рендерим. */
  readonly agentTitle = input<string | null>(null);

  @Output() textChange = new EventEmitter<string>();
  @Output() schemaChange = new EventEmitter<any>();
  @Output() variableNameClick = new EventEmitter<string>();

  @ViewChild('variablesMenu') variablesMenu!: Menu;
  @ViewChild(OpenapiPropertiesEditorComponent) propertiesEditor!: OpenapiPropertiesEditorComponent;

  private dynamicVariablesService = inject(DynamicVariablesService);

  readonly variablesTitle = $localize`:@@variables.title:Variables`;

  /** Описания системных переменных — берём JSDoc-описания из
   *  SystemVariable enum (backend их пишет туда), но enum сгенерирован
   *  и в runtime доступа к JSDoc'ам нет. Держим ручной parallel-map.
   *  При изменении enum'а на бэке — обновлять этот блок вручную. */
  /** Render-функции для system-переменных. Если для переменной задана
   *  функция — считаем preview на лету и показываем в tooltip'е рядом
   *  с описанием. Если функции нет — только описание.
   *  Время/дата — момент, когда юзер открывает меню (approximation
   *  того, что бэк подставит при dispatch'е — точное значение может
   *  отличаться на минуту, но для превью достаточно).
   *  Провайдер / номера / call_sid / session_id — недоступны фронту
   *  до старта звонка, поэтому renderer'ов не имеют. */
  private readonly systemVarRenderers: Partial<Record<SystemVariable, () => string | null>> = {
    system__date_utc: () => new Date().toISOString().slice(0, 10),
    system__time_utc: () => new Date().toISOString().slice(11, 16),
    system__agent_title: () => this.agentTitle() ?? null,
  };

  private readonly systemVarDescriptions: Record<SystemVariable, string> = {
    system__from_number: $localize`:@@variables.system.fromNumber.desc:Number the call is placed from: the agent's own number on an outbound call, the caller's number on an inbound one. Renders as digits, sometimes with a leading plus — "74951182304" or "+79251354348"; empty in a web conversation.`,
    system__to_number: $localize`:@@variables.system.toNumber.desc:Number the call is placed to: the client's number on an outbound call, the agent's own number on an inbound one. Renders as digits, sometimes with a leading plus — "+79251354348"; empty in a web conversation.`,
    system__telecom_provider: $localize`:@@variables.system.telecomProvider.desc:Telephony provider carrying the call. Renders as one of "Twilio", "Voximplant", "Custom"; empty in a web conversation.`,
    system__call_sid: $localize`:@@variables.system.callSid.desc:Provider-side call identifier. Renders in the provider's own shape — "CAf1e2…" for Twilio, a UUID for Voximplant, "SCL_z3TAFY9huv52" for SIP; empty in a web conversation.`,
    system__session_id: $localize`:@@variables.system.sessionId.desc:Identifier of the ongoing conversation, the same one the session is stored under. Renders as a UUID — "019ff67a-a8a6-7d9c-9d4e-2bae203b3269". Always filled.`,
    system__agent_title: $localize`:@@variables.system.agentTitle.desc:Agent's own name as configured in the workspace. Always filled.`,
    system__date_utc: $localize`:@@variables.system.dateUtc.desc:Date the conversation started, UTC. Renders as "2026-08-21". Fixed at dispatch — it does not move during the call.`,
    system__time_utc: $localize`:@@variables.system.timeUtc.desc:Time the conversation started, UTC, 24-hour. Renders as "13:36" — no seconds, no date, no zone suffix. Fixed at dispatch — it does not move during the call.`,
  };

  /** Все переменные из схемы (для меню и справочника). */
  private readonly allSchemaVars = computed<PropertyDefinition[]>(() => {
    const s = this.schema();
    return s ? this.dynamicVariablesService.schemaToProperties(s) : [];
  });

  /** Переменные из ТЕКСТА текущего контрола (для таблицы). */
  readonly variables = computed<PropertyDefinition[]>(() => {
    const names = this.dynamicVariablesService.extractVariablesFromText(this.text());
    if (!names.length) return [];
    return this.allSchemaVars().filter(v => names.includes(v.name));
  });

  /** Меню «+Add Variable» — сначала пункт «New», потом все известные из
   *  схемы, затем divider + группа system-переменных (`system__*`, статичный
   *  enum из бэкенда). Системные не редактируются и не хранятся в схеме
   *  агента — их значения бэк подставляет сам во время звонка. */
  readonly menuItems = computed<MenuItem[]>(() => {
    // p-menu переходит в group-mode как только у ЛЮБОГО top-level item
    // появляется items[]. В group-mode plain root-items становятся
    // некликабельными group-header'ами. Раз system-переменные обёрнуты
    // как группа — обязаны обернуть user-переменные тоже.
    const userItems: MenuItem[] = [{
      label: $localize`:@@variables.addNew:Add New Variable`,
      icon: 'pi pi-plus',
      command: () => this.addNewVariable(),
    }];
    for (const v of this.allSchemaVars()) {
      userItems.push({
        label: `{{${v.name}}}`,
        icon: 'pi pi-code',
        state: { description: this.buildUserVarTooltip(v) },
        command: () => this.insertVariableAtCursor(v.name),
      });
    }

    return [
      {
        label: $localize`:@@variables.userGroup:Variables`,
        items: userItems,
      },
      {
        label: $localize`:@@variables.systemGroup:System variables`,
        items: SYSTEM_VARIABLE.map(name => {
          const preview = this.systemVarRenderers[name]?.() ?? null;
          const description = this.systemVarDescriptions[name];
          const tooltip = preview
            ? $localize`:@@variables.system.previewPrefix:Preview` + `: ${preview}\n\n${description}`
            : description;
          return {
            label: `{{${name}}}`,
            icon: 'pi pi-cog',
            state: { description: tooltip },
            command: () => this.insertVariableAtCursor(name),
          };
        }),
      },
    ];
  });

  /** Составляем tooltip для user-переменной. Preview = default value
   *  (для boolean — true/false, для остальных — как есть; null → skip).
   *  Если ни preview, ни description — возвращаем null, вопросик тогда
   *  не рисуем. */
  private buildUserVarTooltip(v: PropertyDefinition): string | null {
    const preview = this.formatDefaultForPreview(v.default, v.type);
    const description = (v.description ?? '').trim();
    if (!preview && !description) return null;
    const previewLine = preview
      ? $localize`:@@variables.system.previewPrefix:Preview` + `: ${preview}`
      : null;
    return [previewLine, description].filter(Boolean).join('\n\n');
  }

  private formatDefaultForPreview(value: any, type?: string): string | null {
    if (value === null || value === undefined) return null;
    if (type === 'string' && typeof value === 'string' && value.trim() === '') return null;
    if (type === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') {
      try { return JSON.stringify(value); } catch { return null; }
    }
    return String(value);
  }

  onPropertiesChange(properties: PropertyDefinition[]): void {
    // Var'ы, которые НЕ в текущем тексте, оставляем как есть — их могли
    // редактировать в drawer'е соседнего контрола или задать вручную.
    const currentTextVarNames = this.dynamicVariablesService.extractVariablesFromText(this.text());
    const preserved = this.allSchemaVars().filter(v => !currentTextVarNames.includes(v.name));
    const merged = [...preserved, ...properties];
    this.schemaChange.emit(this.dynamicVariablesService.propertiesToSchema(merged));
  }

  onVariableNameClick(property: PropertyDefinition): void {
    this.variableNameClick.emit(property.name);
    this.highlightVariableInText(property.name);
  }

  private highlightVariableInText(variableName: string): void {
    const el = this.textElement();
    const text = this.text();
    if (!el || !text) return;

    const variableText = `{{${variableName}}}`;
    const index = text.indexOf(variableText);
    if (index !== -1) {
      el.focus({ preventScroll: true });
      el.setSelectionRange(index, index + variableText.length);
      el.scrollTop = 0;
    }
  }

  private addNewVariable(): void {
    const name = this.generateUniqueVariableName();
    const newVariable: PropertyDefinition = {
      name,
      title: '',
      type: 'string',
      nullable: false,
      default: '',
    };

    const merged = [...this.allSchemaVars(), newVariable];
    this.insertVariableAtCursor(name);
    this.schemaChange.emit(this.dynamicVariablesService.propertiesToSchema(merged));
  }

  private generateUniqueVariableName(): string {
    const all = this.allSchemaVars();
    let i = 1;
    while (all.some(v => v.name === `variable${i}`)) i++;
    return `variable${i}`;
  }

  private insertVariableAtCursor(variableName: string): void {
    const el = this.textElement();
    if (!el) return;

    // Читаем и позицию курсора, и текст из САМОГО DOM-элемента — иначе
    // при рассинхроне между text()-signal'ом (обновляется через input-
    // event async'но) и el.value подставится «вчерашний» текст и вставка
    // уедет не туда.
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const variableText = `{{${variableName}}}`;
    const currentText = el.value ?? '';
    const newText = currentText.substring(0, start) + variableText + currentText.substring(end);

    // Синхронизируем DOM (для ngModel/CVA) + сигнал parent'а обновится
    // через input-event → директива подхватит новый text().
    el.value = newText;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    this.textChange.emit(newText);

    // Cursor после вставленной переменной. preventScroll — иначе браузер
    // при focus() докручивает страницу к элементу, а с autoResize=true у
    // textarea высота уже пересчиталась, и получается видимый прыжок.
    setTimeout(() => {
      el.focus({ preventScroll: true });
      const pos = start + variableText.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  }
}
